'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import AnimatedLinesBackground from '@/components/AnimatedLinesBackground';
import { ArrowRightIcon } from '@/assets';

/**
 * Props for `HeroHeadlineCta`.
 */
export type HeroHeadlineCtaProps =
  SliceComponentProps<Content.HeroHeadlineCtaSlice>;

/**
 * Component for "HeroHeadlineCta" Slices.
 */
const HeroHeadlineCta: FC<HeroHeadlineCtaProps> = ({ slice }) => {
  const { headline, supporting_text, primary_cta, breadcrumbs } = slice.primary;

  // Handle breadcrumbs rendering
  const renderBreadcrumbs = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    return (
      <nav className="w-full px-4 md:px-6 mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          {breadcrumbs.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              {index > 0 && <span>/</span>}
              {item.breadcrumb_link ? (
                <PrismicNextLink
                  field={item.breadcrumb_link}
                  className="hover:text-white transition-colors"
                >
                  {item.breadcrumb_text}
                </PrismicNextLink>
              ) : (
                <span className="text-white">{item.breadcrumb_text}</span>
              )}
            </div>
          ))}
        </div>
      </nav>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white pt-48 pb-24 md:pt-48 md:pb-40 overflow-hidden"
    >
      {/* Temporary background with lower z-index so animated lines show through */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          background: 'linear-gradient(180deg, #0E1010 0%, #0F423A 100%)',
          zIndex: -2,
        }}
      />

      <AnimatedLinesBackground />

      <div className="relative z-10 container mx-auto flex flex-col items-center text-center gap-8">
        {/* Breadcrumbs */}
        {renderBreadcrumbs()}

        <div className="px-4 md:px-32 flex flex-col gap-6">
          <div className="font-bold text-4xl md:text-6xl">
            <PrismicRichText
              field={headline}
              components={{
                heading1: ({ children }) => (
                  <h1 className="h1-hero">{children}</h1>
                ),
              }}
            />
          </div>
          <div className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            <PrismicRichText field={supporting_text} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {primary_cta && (
            <PrismicNextLink field={primary_cta} className="secondary-button">
              <ArrowRightIcon className="arrow-icon" />
              {primary_cta.text || 'Learn More'}
            </PrismicNextLink>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroHeadlineCta;
